// Pestaña de cargas del pedido.
//
// El contenido es `CargasPedido` tal cual —las mismas filas, los mismos badges y
// los mismos dos cajones que la lista—, sin su marco: aquí los filtros, el
// totalizador y el botón de crear los pone la pestaña.
//
// SEMÁNTICA DE LOS FILTROS: dentro de uno, OR —dos artículos son «este o
// este»—; entre filtros distintos, AND. Todos salen de datos que ya están en
// memoria: ninguno pide nada al servidor.
//
// Y el filtro de artículos no sólo esconde filas: abre las cargas que casan y
// resalta dentro la referencia que las trajo, para que se vea POR QUÉ está cada
// carga en la lista. Después el usuario puede colapsarlas a mano.
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import {
  agruparPorCarga, filtrarPorEstado, totalPicking, totalPesos,
} from '@/utils/cargasPicking'
import { CargasPedido, avisoPeso } from '../CargasPedido'
import { fmtQty, fmtPeso, fmtCajas } from '../formato'
import { MultiFiltro, type OpcionMulti } from '@/components/common/MultiFiltro'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import {
  BarraFiltrosActivos, descripcionDe, filtrosDe, type FiltroActivo,
} from '@/components/common/BarraFiltrosActivos'
import { BarraMarcas, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import {
  Franja, VacioDiscreto, btnSecundarioCls, pildoraCls, type DatoFranja,
} from './controles'
import { bordeTenueCls, btnPrimarioCls } from './estilos'
import type {
  CargaCreateResult, CargaLinea, FiltroEstadoCarga, PedidoVentaLinea, SolicitudCarga,
} from '@/types'

interface Props {
  dark: boolean
  empresa?: string
  numPedido: string
  cargas: CargaLinea[]
  lineas: PedidoVentaLinea[]
  solicitudes: SolicitudCarga[]
  lineaPorTransid: Map<string, number | undefined>
  descripcionPorTransid: Map<string, string | undefined>
  filtro: FiltroEstadoCarga
  onFiltro: (f: FiltroEstadoCarga) => void
  puedeCrear: boolean
  nuevaAbierta: boolean
  onNuevaAbierta: (abierta: boolean) => void
  onSolicitada: (resultado: CargaCreateResult) => void
}

/** Los tres estados que el dato permite distinguir: el origen no tiene más.
 *  Nada de inventar «en tránsito» o «entregada», que no existen en la tabla. */
const ESTADOS: { valor: FiltroEstadoCarga; clave: string }[] = [
  { valor: '',         clave: 'common.allFem' },
  { valor: 'almacen',  clave: 'salesOrders.picking.enAlmacen' },
  { valor: 'expedida', clave: 'salesOrders.picking.expedida' },
]

export function SeccionCargas({
  dark, empresa, numPedido, cargas, lineas, solicitudes, lineaPorTransid,
  descripcionPorTransid, filtro, onFiltro, puedeCrear, nuevaAbierta,
  onNuevaAbierta, onSolicitada,
}: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  // ── Marcas de fila ──────────────────────────────────────────────────────────
  // Ámbito PROPIO, y no el de la pestaña de Líneas (`pedidoVentaLineas:…`),
  // aunque las dos hablen del mismo pedido. La franja de recuento vive dentro de
  // la pestaña, así que un ámbito común diría aquí «7 marcadas» contando filas
  // que están en otra pestaña y que desde aquí no se ven; y su «quitar todas»
  // borraría de un clic el trabajo hecho allí. Además el grano es otro: aquí se
  // marca una carga o una fila (carga × artículo), allí una línea de venta.
  const marcas = useMarcasFilas(`pedidoVentaCargas:${empresa ?? ''}/${numPedido}`)
  // El ámbito ya fija el pedido, pero la clave se construye con el MISMO esquema
  // que la lista (empresa + nº de pedido, y encima el nº de carga): así una
  // carga se nombra igual en las dos pantallas y las dos se pueden comparar.
  const clavePedido = useMemo(() => claveFila(empresa ?? '', numPedido), [empresa, numPedido])

  const [articulos, setArticulos] = useState<string[]>([])
  const [refs, setRefs] = useState<string[]>([])
  const [expansion, setExpansion] = useState<{
    modo: 'auto' | 'todas' | 'ninguna'
    nonce: number
  }>({ modo: 'auto', nonce: 0 })

  // Artículos que están en alguna carga de este pedido.
  const opcionesArticulo: OpcionMulti[] = useMemo(() => {
    const vistos = new Map<string, string>()
    for (const c of cargas) {
      if (!vistos.has(c.articulo)) {
        vistos.set(c.articulo, descripcionPorTransid.get(c.transid) ?? '')
      }
    }
    return [...vistos.entries()]
      .map(([codigo, descripcion]) => ({
        valor: codigo, titulo: fmtArt(codigo), detalle: descripcion,
      }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es-ES'))
  }, [cargas, descripcionPorTransid, fmtArt])

  // Números de carga y destinos, en la misma lista: son las dos formas de
  // nombrar una carga que el usuario ve en la fila.
  const opcionesRef: OpcionMulti[] = useMemo(() => {
    const numeros = new Map<string, string>()
    const destinos = new Set<string>()
    for (const c of cargas) {
      if (!numeros.has(c.carga)) numeros.set(c.carga, c.referenciaCliente ?? '')
      if (c.referenciaCliente?.trim()) destinos.add(c.referenciaCliente.trim())
      if (c.referenciaCliente2?.trim()) destinos.add(c.referenciaCliente2.trim())
    }
    return [
      ...[...numeros.entries()]
        .map(([carga, destino]) => ({ valor: `carga:${carga}`, titulo: carga, detalle: destino }))
        .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es-ES')),
      ...[...destinos]
        .sort((a, b) => a.localeCompare(b, 'es-ES'))
        .map(d => ({ valor: `ref:${d}`, titulo: d, detalle: t('salesOrders.detalle.destino') })),
    ]
  }, [cargas, t])

  const elegidosArt = useMemo(() => new Set(articulos), [articulos])

  // El filtro de artículo se aplica a la CARGA entera: si lleva el artículo
  // buscado se enseña completa, porque lo que se busca es «en qué cargas va
  // esto», no «qué parte de la carga es esto».
  const cargasConArticulo = useMemo(() => {
    if (elegidosArt.size === 0) return null
    return new Set(cargas.filter(c => elegidosArt.has(c.articulo)).map(c => c.carga))
  }, [cargas, elegidosArt])

  const cargasConRef = useMemo(() => {
    if (refs.length === 0) return null
    const numeros = new Set(refs.filter(r => r.startsWith('carga:')).map(r => r.slice(6)))
    const destinos = new Set(refs.filter(r => r.startsWith('ref:')).map(r => r.slice(4)))
    return new Set(
      cargas
        .filter(c => numeros.has(c.carga)
          || (c.referenciaCliente && destinos.has(c.referenciaCliente.trim()))
          || (c.referenciaCliente2 && destinos.has(c.referenciaCliente2.trim())))
        .map(c => c.carga),
    )
  }, [cargas, refs])

  const filtradas = useMemo(
    () => filtrarPorEstado(cargas, filtro).filter(c =>
      (!cargasConArticulo || cargasConArticulo.has(c.carga))
      && (!cargasConRef || cargasConRef.has(c.carga))),
    [cargas, filtro, cargasConArticulo, cargasConRef],
  )

  // Resalta dentro de la carga la línea que la trajo a la lista.
  const coincide = useMemo(
    () => (elegidosArt.size === 0
      ? null
      : (articulo?: string) => !!articulo && elegidosArt.has(articulo)),
    [elegidosArt],
  )

  // Al cambiar la selección de artículos se vuelve al modo automático y se
  // olvidan los colapsos manuales: las cargas nuevas deben abrirse solas para
  // enseñar por qué han aparecido.
  useEffect(() => {
    setExpansion(e => ({ modo: 'auto', nonce: e.nonce + 1 }))
  }, [articulos])

  const totalesPicking = useMemo(() => totalPicking(filtradas), [filtradas])
  const pesosCargas = useMemo(() => totalPesos(filtradas), [filtradas])
  const avisoPesos = avisoPeso(pesosCargas, t)
  const grupos = useMemo(() => agruparPorCarga(filtradas), [filtradas])
  const expedidas = grupos.filter(g => g.expedida).length
  const enAlmacen = grupos.length - expedidas
  const numTotales = useMemo(() => new Set(cargas.map(c => c.carga)).size, [cargas])

  // También los multi-selección: el campo sólo lleva un contador, así que sin
  // esta franja no se podría ver ni quitar una selección concreta.
  const filtrosActivos: FiltroActivo[] = [
    ...filtrosDe(refs, setRefs, {
      campo: t('salesOrders.detalle.cargaCol'),
      tooltipDe: descripcionDe(opcionesRef),
      prefijo: 'c:',
    }),
    ...filtrosDe(articulos, setArticulos, {
      campo: t('salesOrders.line.article'),
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      tooltipDe: descripcionDe(opcionesArticulo),
      prefijo: 'a:',
    }),
    ...(filtro
      ? [{
          clave: 'est', campo: t('common.status'),
          valor: t(filtro === 'expedida' ? 'salesOrders.picking.expedida' : 'salesOrders.picking.enAlmacen'),
          quitar: () => onFiltro('' as FiltroEstadoCarga),
        }]
      : []),
  ]

  const hayFiltros = filtrosActivos.length > 0

  function limpiar() {
    setArticulos([]); setRefs([]); onFiltro('')
  }

  const totales: DatoFranja[] = [
    {
      clave: 'cargas',
      valor: hayFiltros
        ? t('salesOrders.detalle.mostrando', { n: grupos.length, total: numTotales })
        : String(numTotales),
      etiqueta: t('salesOrders.detalle.cargasCol').toLowerCase(),
    },
    ...(expedidas > 0 ? [{
      clave: 'exp', valor: String(expedidas), etiqueta: t('salesOrders.detalle.expedidasEtiqueta'),
    }] : []),
    ...(enAlmacen > 0 ? [{
      clave: 'alm', valor: String(enAlmacen), tono: 'ambar' as const,
      etiqueta: t('salesOrders.detalle.enAlmacenEtiqueta'),
    }] : []),
    {
      clave: 'ud', valor: fmtQty(totalesPicking.solicitada),
      etiqueta: `${t('salesOrders.picking.units')} ${t('salesOrders.detalle.lanzadas')}`,
    },
    // Bultos y peso de las cargas visibles. Se suman sobre las mismas filas que
    // las unidades de al lado, así que un filtro mueve las cuatro cifras a la vez.
    //
    // El ámbar NO es un estado de la carga: dice que este total está incompleto
    // o es incoherente porque al maestro de artículos le falta el peso. Un total
    // parcial pintado como total es lo que acaba en un transporte.
    ...(pesosCargas.cajas != null ? [{
      clave: 'cajas', valor: fmtCajas(pesosCargas.cajas),
      etiqueta: t('salesOrders.picking.cajas').toLowerCase(),
      titulo: t('salesOrders.picking.cajasTooltip'),
    }] : []),
    {
      clave: 'neto', valor: `${fmtPeso(pesosCargas.neto)} ${t('salesOrders.picking.kg')}`,
      etiqueta: t('salesOrders.picking.neto').toLowerCase(),
      tono: avisoPesos ? 'ambar' as const : undefined,
      titulo: avisoPesos || t('salesOrders.picking.pesoTooltip'),
    },
    {
      clave: 'bruto', valor: `${fmtPeso(pesosCargas.bruto)} ${t('salesOrders.picking.kg')}`,
      etiqueta: t('salesOrders.picking.bruto').toLowerCase(),
      tono: avisoPesos ? 'ambar' as const : undefined,
      titulo: avisoPesos || t('salesOrders.picking.pesoTooltip'),
    },
  ]

  const todasAbiertas = expansion.modo === 'todas'

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <MultiFiltro
          dark={dark}
          opciones={opcionesRef}
          seleccion={refs}
          onSeleccion={setRefs}
          placeholder={t('salesOrders.detalle.buscarCarga')}
          className="w-[230px]"
        />
        <MultiFiltro
          dark={dark}
          opciones={opcionesArticulo}
          seleccion={articulos}
          onSeleccion={setArticulos}
          placeholder={t('salesOrders.detalle.filtrarArticulos')}
          className="w-[240px]"
        />

        <div className="flex items-center gap-1">
          {ESTADOS.map(e => (
            <button
              key={e.valor || 'todas'}
              type="button"
              onClick={() => onFiltro(e.valor)}
              aria-pressed={filtro === e.valor}
              className={pildoraCls(dark, filtro === e.valor)}
            >
              {t(e.clave)}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Acción secundaria: sólo afecta a las cargas visibles. */}
          {grupos.length > 0 && (
            <button
              type="button"
              onClick={() => setExpansion(e => ({
                modo: todasAbiertas ? 'ninguna' : 'todas',
                nonce: e.nonce + 1,
              }))}
              className={btnSecundarioCls(dark)}
            >
              {t(todasAbiertas
                ? 'salesOrders.detalle.colapsarTodo'
                : 'salesOrders.detalle.expandirTodo')}
            </button>
          )}
          {puedeCrear && (
            <button type="button" onClick={() => onNuevaAbierta(true)} className={btnPrimarioCls}>
              {t('salesOrders.nuevaCarga.boton')}
            </button>
          )}
        </div>
      </div>

      {hayFiltros && (
        <BarraFiltrosActivos dark={dark} filtros={filtrosActivos} onLimpiar={limpiar} />
      )}

      {/* Junto a la de filtros y por encima del totalizador, igual que en la
          pestaña de Líneas: las dos franjas dicen «esto lo has puesto tú encima
          del dato» y ninguna engorda la barra de controles. */}
      <BarraMarcas dark={dark} marcas={marcas} />

      <Franja dark={dark} datos={totales} className={`pb-2 border-b ${bordeTenueCls(dark)}`} />

      {filtradas.length === 0 && solicitudes.length === 0 && hayFiltros ? (
        <VacioDiscreto
          dark={dark}
          texto={t('salesOrders.detalle.sinCargasFiltro')}
          accion={{ texto: t('salesOrders.detalle.limpiarTodos'), onClick: limpiar }}
        />
      ) : (
        <CargasPedido
          variante="pagina"
          dark={dark}
          marcas={marcas}
          clavePedido={clavePedido}
          cargas={filtradas}
          lineaPorTransid={lineaPorTransid}
          descripcionPorTransid={descripcionPorTransid}
          coincide={coincide}
          expansion={expansion}
          empresa={empresa}
          numPedido={numPedido}
          lineas={lineas}
          solicitudes={solicitudes}
          puedeCrear={puedeCrear}
          abrirNueva={nuevaAbierta}
          onAbrirNueva={onNuevaAbierta}
          onSolicitada={onSolicitada}
        />
      )}
    </div>
  )
}
